import { Countries } from '@/utils/common';

/**
 * Constantes de países para uso en toda la aplicación
 */
export const COUNTRY_MEX: Countries = 'MEX';
export const COUNTRY_COL: Countries = 'COL';
export const COUNTRY_CHL: Countries = 'CHL';
export const COUNTRY_PER: Countries = 'PER';

/**
 * Países que permiten cancelar órdenes en el modal de detalles
 */
export const COUNTRIES_WITH_CANCEL_POSSIBILITY: Countries[] = [
  COUNTRY_MEX,
  COUNTRY_COL,
  COUNTRY_CHL,
  COUNTRY_PER,
];

/**
 * Tipo de monedas soportadas
 */
export type Currency = 'MXN' | 'COP' | 'PEN' | 'CLP';

/**
 * Configuración de moneda por país
 */
export interface CountryCurrencyConfig {
  /** Código del país */
  code: Countries;
  /** Código de moneda ISO */
  currencyCode: Currency;
  /** Símbolo de moneda */
  currencySymbol: string;
  /** Locale para formateo */
  locale: string;
}

/**
 * Configuración de monedas por país
 */
export const COUNTRY_CURRENCY_CONFIG: Record<Countries, CountryCurrencyConfig> = {
  MEX: {
    code: 'MEX',
    currencyCode: 'MXN',
    currencySymbol: '$',
    locale: 'es-MX',
  },
  COL: {
    code: 'COL',
    currencyCode: 'COP',
    currencySymbol: '$',
    locale: 'es-CO',
  },
  CHL: {
    code: 'CHL',
    currencyCode: 'CLP',
    currencySymbol: '$',
    locale: 'es-CL',
  },
  PER: {
    code: 'PER',
    currencyCode: 'PEN',
    currencySymbol: 'S/',
    locale: 'es-PE',
  },
};

/**
 * Configuración de validación de códigos postales por país
 */
export interface CountryZipCodeConfig {
  /** Código del país */
  code: Countries;
  /** Nombre del país */
  name: string;
  /** Si el código postal es obligatorio */
  required: boolean;
  /** Longitud del código postal */
  length: number;
  /** Patrón regex para validación */
  pattern: RegExp;
  /** Mensaje de error */
  errorMessage: string;
}

/**
 * Configuración de códigos postales por país
 */
export const COUNTRY_ZIPCODE_CONFIG: Record<Countries, CountryZipCodeConfig> = {
  MEX: {
    code: 'MEX',
    name: 'México',
    required: true,
    length: 5,
    pattern: /^\d{5}$/,
    errorMessage: 'El código postal debe tener 5 dígitos numéricos',
  },
  COL: {
    code: 'COL',
    name: 'Colombia',
    required: true,
    length: 6,
    pattern: /^\d{6}$/,
    errorMessage: 'El código postal debe tener 6 dígitos numéricos',
  },
  CHL: {
    code: 'CHL',
    name: 'Chile',
    required: false,
    length: 7,
    pattern: /^\d{7}$/,
    errorMessage: 'El código postal debe tener 7 dígitos numéricos',
  },
  PER: {
    code: 'PER',
    name: 'Perú',
    required: false,
    length: 5,
    pattern: /^\d{5}$/,
    errorMessage: 'El código postal debe tener 5 dígitos numéricos',
  },
};

/**
 * Lista de países que requieren código postal obligatorio
 */
export const COUNTRIES_REQUIRING_ZIPCODE: Countries[] = Object.values(COUNTRY_ZIPCODE_CONFIG)
  .filter((config) => config.required)
  .map((config) => config.code);

/**
 * Verifica si un país requiere código postal obligatorio
 * @param country - Código del país
 * @returns true si el país requiere código postal obligatorio
 */
export const countryRequiresZipCode = (country?: Countries): boolean => {
  if (!country) return false;
  return COUNTRY_ZIPCODE_CONFIG[country]?.required ?? false;
};

/**
 * Obtiene la configuración de código postal para un país
 * @param country - Código del país
 * @returns Configuración de código postal del país
 */
export const getCountryZipCodeConfig = (country?: Countries): CountryZipCodeConfig => {
  return COUNTRY_ZIPCODE_CONFIG[country || 'MEX'];
};

/**
 * Obtiene la configuración de moneda para un país
 * @param country - Código del país
 * @returns Configuración de moneda del país
 */
export const getCountryCurrencyConfig = (country?: Countries): CountryCurrencyConfig => {
  return COUNTRY_CURRENCY_CONFIG[country || 'MEX'];
};

/**
 * Obtiene el código de moneda para un país
 * @param country - Código del país
 * @returns Código de moneda (ej: 'MXN', 'COP')
 */
export const getCurrencyCode = (country?: Countries): Currency => {
  return COUNTRY_CURRENCY_CONFIG[country || 'MEX'].currencyCode;
};

/**
 * Obtiene el símbolo de moneda para un país
 * @param country - Código del país
 * @returns Símbolo de moneda (ej: '$', 'S/')
 */
export const getCurrencySymbol = (country?: Countries): string => {
  return COUNTRY_CURRENCY_CONFIG[country || 'MEX'].currencySymbol;
};

export const COUNTRY_DIAL_CODES: Record<Countries, string> = {
  MEX: '+52',
  COL: '+57',
  CHL: '+56',
  PER: '+51',
};

export const getDialCode = (country?: Countries): string => COUNTRY_DIAL_CODES[country || 'MEX'];
